import PropTypes from "prop-types";
import { useState } from "react";
import { useUserDetails } from "../../hooks/useUserDetails.js";
import { getUserTypeConfig, getStatusConfig } from "../../utils";
import { formatDate, isActive } from "../../utils";
import { Icons } from "../../utils/icons.jsx";
import DomainBadge from "../common/DomainBadge.jsx";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal.jsx";
import Modal from "../common/Modal.jsx";
import { useAppMode } from "../../context/AppMode.jsx";

function UserCard({ userId, onClose, onEdit, onDelete, onSuccess }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const { mode } = useAppMode();

  const { user, loading, error, reload } = useUserDetails(userId);

  const handleSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
    reload();
    onSuccess?.();
  };

  const handleError = (err) => {
    setErrorMessage(err?.message || String(err));
    setTimeout(() => setErrorMessage(null), 5000);
  };

  const confirmDelete = () => setShowDeleteModal(true);
  const cancelDelete = () => setShowDeleteModal(false);

  const handleDeleteUser = async () => {
    try {
      await onDelete(user);
      handleSuccess("Adhérent supprimé avec succès");
      setShowDeleteModal(false);
      onClose();
    } catch (err) {
      handleError(err);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Modal isOpen onClose={onClose} size="lg">
        <div className="loading">
          <Icons.Loading className="spin" />
          <p>Chargement...</p>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal isOpen onClose={onClose} size="sm">
        <div className="error">
          <Icons.Warning />
          <h3>Membre non trouvé</h3>
          <p>{error}</p>
        </div>
      </Modal>
    );
  }

  const userTypeConfig = getUserTypeConfig(user.user_type);

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        title={
          <div className="modal-header-content">
            <div className="info-card-avatar">
              <Icons.User />
            </div>
            <div className="modal-header-text">
              <h2>
                {user.first_name} {user.last_name}
              </h2>
              <div className="modal-card-header">
                {userTypeConfig && <DomainBadge config={userTypeConfig} />}
              </div>
            </div>
            <div className="header-actions-group">
              <button
                className="btn-icon-modern"
                type="button"
                onClick={() => onEdit(user)}
                title="Modifier"
              >
                <Icons.Edit />
              </button>
              {mode === "admin" && (
                <button
                  type="button"
                  className="btn-icon-modern danger"
                  onClick={confirmDelete}
                  title="Supprimer"
                >
                  <Icons.Delete />
                </button>
              )}
            </div>
          </div>
        }
      >
        <div className="modal-content-scrollable">
          {successMessage && (
            <div className="alert alert-success">
              <Icons.Check /> {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="alert alert-error">
              <Icons.Warning /> {errorMessage}
            </div>
          )}

          <div className="card">
            <div className="info-card">
              <div className="info-card-header">
                <h3>Coordonnées</h3>
              </div>
              <div className="info-card-body flex-row">
                <div className="info-item-modern">
                  <span className="info-label">Email</span>
                  <span className="info-value">{user.email || "-"}</span>
                </div>
                <div className="info-item-modern">
                  <span className="info-label">Téléphone</span>
                  <span className="info-value">{user.phone || "-"}</span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-header">
                <h3>Activité</h3>
              </div>
              <div className="info-card-body">
                <div className="info-item-modern">
                  <span className="info-label">Début</span>
                  <span className="info-value">
                    {user.activity_start_date
                      ? formatDate(user.activity_start_date)
                      : "-"}
                  </span>
                </div>
                {user.activity_end_date ? (
                  <div className="info-item-modern">
                    <span className="info-label">Fin</span>
                    <span className="info-value">
                      {formatDate(user.activity_end_date)}
                    </span>
                  </div>
                ) : (
                  <div className="info-item-modern">
                    <span className="info-label">En activité</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onDelete={handleDeleteUser}
        itemType="user"
        itemName={`${user.first_name} ${user.last_name}`}
      />
    </>
  );
}

UserCard.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default UserCard;
